import { supabase } from "@/integrations/supabase/client";

export interface ReconciliationResult {
  success: boolean;
  cardTransactionId?: string;
  matchType?: 'nsu' | 'authorization_code' | 'amount_date';
  message: string;
}

interface CardTransactionMatch {
  id: string;
  nsu: string | null;
  authorization_code: string | null;
  amount: number;
  transaction_date: string;
}

interface ReconciliationLogParams {
  companyId: string;
  salePaymentId: string;
  cardTransactionId?: string;
  status: 'auto_matched' | 'manual' | 'failed' | 'pending';
  matchType?: 'nsu' | 'authorization_code' | 'amount_date' | 'manual' | null;
  gonxAmount: number;
  terminalAmount?: number;
  nsu?: string;
  authorizationCode?: string;
  notes?: string;
  userId?: string;
}

/**
 * Busca uma transação de cartão correspondente ao pagamento
 * Prioridade: NSU > Authorization Code > Amount + Date
 */
export async function findMatchingCardTransaction(
  companyId: string,
  nsu?: string,
  authorizationCode?: string,
  amount?: number,
  transactionDate?: string
): Promise<CardTransactionMatch | null> {
  // 1. Tentar match por NSU (mais confiável)
  if (nsu) {
    const { data: byNsu } = await supabase
      .from("card_transactions")
      .select("id, nsu, authorization_code, amount, transaction_date")
      .eq("company_id", companyId)
      .eq("nsu", nsu)
      .limit(1)
      .single();
    
    if (byNsu) {
      return byNsu as CardTransactionMatch;
    }
  }

  // 2. Tentar match por código de autorização
  if (authorizationCode) {
    const { data: byAuth } = await supabase
      .from("card_transactions")
      .select("id, nsu, authorization_code, amount, transaction_date")
      .eq("company_id", companyId)
      .eq("authorization_code", authorizationCode)
      .limit(1)
      .single();
    
    if (byAuth) {
      return byAuth as CardTransactionMatch;
    }
  }

  // 3. Tentar match por valor + data (menos confiável, mas útil)
  if (amount && transactionDate) {
    const dateOnly = transactionDate.split('T')[0];
    const { data: byAmountDate } = await supabase
      .from("card_transactions")
      .select("id, nsu, authorization_code, amount, transaction_date")
      .eq("company_id", companyId)
      .gte("transaction_date", `${dateOnly}T00:00:00`)
      .lte("transaction_date", `${dateOnly}T23:59:59`)
      .gte("amount", amount - 0.01)
      .lte("amount", amount + 0.01)
      .limit(1)
      .single();
    
    if (byAmountDate) {
      return byAmountDate as CardTransactionMatch;
    }
  }

  return null;
}

/**
 * Vincula um pagamento a uma transação de cartão
 */
export async function linkPaymentToTransaction(
  salePaymentId: string,
  cardTransactionId: string
): Promise<boolean> {
  const { error } = await supabase
    .from("sale_payments")
    .update({ card_transaction_id: cardTransactionId } as any)
    .eq("id", salePaymentId);
  
  return !error;
}

/**
 * Registra log de conciliação para auditoria
 */
export async function logReconciliation(
  params: ReconciliationLogParams
): Promise<void> {
  const difference = params.terminalAmount 
    ? Math.abs(params.gonxAmount - params.terminalAmount) 
    : null;

  await supabase
    .from("card_reconciliation_logs" as any)
    .insert({
      company_id: params.companyId,
      sale_payment_id: params.salePaymentId,
      card_transaction_id: params.cardTransactionId || null,
      status: params.status,
      match_type: params.matchType || null,
      gonx_amount: params.gonxAmount,
      terminal_amount: params.terminalAmount || null,
      difference,
      nsu: params.nsu || null,
      authorization_code: params.authorizationCode || null,
      notes: params.notes || null,
      created_by: params.userId || null,
    });
}

/**
 * Função principal de conciliação automática
 * Chamada após um pagamento Smart POS ser registrado com sucesso
 */
export async function autoReconcilePayment(
  salePaymentId: string,
  companyId: string,
  paymentData: {
    nsu?: string;
    authorizationCode?: string;
    amount: number;
    paymentDate: string;
  },
  userId?: string
): Promise<ReconciliationResult> {
  try {
    // Buscar transação correspondente
    const matchedTransaction = await findMatchingCardTransaction(
      companyId,
      paymentData.nsu,
      paymentData.authorizationCode,
      paymentData.amount,
      paymentData.paymentDate
    );

    if (!matchedTransaction) {
      // Nenhuma transação encontrada ainda - pode não ter sincronizado
      await logReconciliation({
        companyId,
        salePaymentId,
        status: 'pending',
        matchType: null,
        gonxAmount: paymentData.amount,
        nsu: paymentData.nsu,
        authorizationCode: paymentData.authorizationCode,
        notes: 'Aguardando sincronização de transações do terminal',
        userId,
      });

      return {
        success: false,
        message: 'Transação de terminal não encontrada - aguardando sincronização',
      };
    }

    // Determinar tipo de match
    let matchType: 'nsu' | 'authorization_code' | 'amount_date';
    if (paymentData.nsu && matchedTransaction.nsu === paymentData.nsu) {
      matchType = 'nsu';
    } else if (paymentData.authorizationCode && matchedTransaction.authorization_code === paymentData.authorizationCode) {
      matchType = 'authorization_code';
    } else {
      matchType = 'amount_date';
    }

    // Vincular pagamento à transação
    const linked = await linkPaymentToTransaction(salePaymentId, matchedTransaction.id);
    
    if (!linked) {
      await logReconciliation({
        companyId,
        salePaymentId,
        cardTransactionId: matchedTransaction.id,
        status: 'failed',
        matchType,
        gonxAmount: paymentData.amount,
        terminalAmount: matchedTransaction.amount,
        nsu: paymentData.nsu,
        authorizationCode: paymentData.authorizationCode,
        notes: 'Falha ao vincular pagamento à transação',
        userId,
      });

      return {
        success: false,
        message: 'Falha ao vincular pagamento à transação',
      };
    }

    // Registrar sucesso
    await logReconciliation({
      companyId,
      salePaymentId,
      cardTransactionId: matchedTransaction.id,
      status: 'auto_matched',
      matchType,
      gonxAmount: paymentData.amount,
      terminalAmount: matchedTransaction.amount,
      nsu: paymentData.nsu || matchedTransaction.nsu || undefined,
      authorizationCode: paymentData.authorizationCode || matchedTransaction.authorization_code || undefined,
      notes: `Conciliação automática por ${matchType}`,
      userId,
    });

    return {
      success: true,
      cardTransactionId: matchedTransaction.id,
      matchType,
      message: `Conciliado automaticamente por ${matchType}`,
    };
  } catch (error) {
    console.error('Erro na conciliação automática:', error);
    
    // Tentar registrar falha
    try {
      await logReconciliation({
        companyId,
        salePaymentId,
        status: 'failed',
        matchType: null,
        gonxAmount: paymentData.amount,
        nsu: paymentData.nsu,
        authorizationCode: paymentData.authorizationCode,
        notes: `Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
        userId,
      });
    } catch {
      // Ignore log errors
    }

    return {
      success: false,
      message: error instanceof Error ? error.message : 'Erro na conciliação automática',
    };
  }
}

/**
 * Vincula manualmente um pagamento a uma transação de cartão
 * Para uso no dashboard de conciliação
 */
export async function manualReconcilePayment(
  salePaymentId: string,
  cardTransactionId: string,
  companyId: string,
  gonxAmount: number,
  terminalAmount: number,
  userId?: string
): Promise<ReconciliationResult> {
  try {
    // Vincular
    const linked = await linkPaymentToTransaction(salePaymentId, cardTransactionId);
    
    if (!linked) {
      return {
        success: false,
        message: 'Falha ao vincular pagamento à transação',
      };
    }

    // Registrar log
    await logReconciliation({
      companyId,
      salePaymentId,
      cardTransactionId,
      status: 'manual',
      matchType: 'manual',
      gonxAmount,
      terminalAmount,
      notes: 'Conciliação manual pelo usuário',
      userId,
    });

    return {
      success: true,
      cardTransactionId,
      message: 'Vinculado manualmente com sucesso',
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Erro ao vincular manualmente',
    };
  }
}
